import React, { useEffect, useMemo, useState } from "react";
import {
  TableRow,
  TableCell,
  Button,
  Chip,
  Typography,
  IconButton,
  CircularProgress,
  Dialog,
  DialogContent,
  AppBar,
  Toolbar,
  Stack,
  Tooltip,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { Download, Clear, Chat } from "@mui/icons-material";
import { statusMap } from "../InboxPage";
import { enqueueSnackbar } from "notistack";
import ReviewPage from "../../../Components/ReviewPage/ReviewPage";
import DownloadIcon from "./u864.png";
import { BACKEND_URL, CONSUMER_URL } from "../../../helpers/variables";
import AgentChat from "./AgentChat";
import { useLoaderData } from "react-router-dom";
import { saveAs } from "file-saver";

export function FileRow({ file, email, refresh }) {
  let { dev_mode = false } = useLoaderData();

  const [downloadFile, setDownloadFile] = useState(null);
  const [selectedBill, setSelectedBill] = useState(null);

  const [fileAgentChat, setFileAgentChat] = useState(null);
  const [billAgentChat, setBillAgentChat] = useState(null);

  async function getFileAgentChat(file_table_id) {
    let res = await fetch(
      `${BACKEND_URL}/file_agent_chat?file_table_id=${file_table_id}`
    );
    let data = await res.json();
    setFileAgentChat(data);
  }

  async function getBillAgentChat(bill_id) {
    let res = await fetch(`${BACKEND_URL}/bill_agent_chat?bill_id=${bill_id}`);
    let data = await res.json();
    setBillAgentChat(data);
  }

  async function downloadS3File(file) {
    setDownloadFile(file);

    let res = await fetch(`${CONSUMER_URL}/dowload_file?s3_key=${file["s3_key"]}`)
    let blob = await res.blob();
    saveAs(blob, file["source_filename"]);

    setDownloadFile(null);
  }

  const bills = file["bills"];
  bills.sort((a, b) => a.page_numbers[0] - b.page_numbers[0]);

  return (
    <>
      {bills.length === 0 ? (
        <TableRow key={file["_id"]} sx={{ bgcolor: grey[100] }}>
          <TableCell
            colSpan={6}
            sx={{ borderRight: `1px solid ${grey[300]}` }}
            onClick={() => dev_mode && getFileAgentChat(file["_id"])}
          >
            <Typography
              fontWeight={"bold"}
              fontSize={13}
              color={"text.secondary"}
              sx={{ wordBreak: "break-all" }}
            >
              {file["source_filename"]}
            </Typography>
          </TableCell>
          <TableCell>Processing</TableCell>
          <TableCell
            sx={{ borderLeft: `1px solid ${grey[300]}` }}
            align="center"
          >
            {file["_id"] === downloadFile?.["_id"] ? (
              <CircularProgress />
            ) : (
              <IconButton onClick={() => downloadS3File(file)}>
                <img src={DownloadIcon} style={{ width: 22, height: 22 }} />
              </IconButton>
            )}
          </TableCell>
        </TableRow>
      ) : (
        bills.map((bill, index) => {
          let bill_id = bill["_id"];
          let bill_status = bill["bill_status"];
          let isApprove = bill_status === 1;

          let status = null;
          if (typeof bill_status !== "undefined")
            status = statusMap[bill_status];

          return (
            <TableRow key={bill_id} sx={{ bgcolor: grey[100] }}>
              {index === 0 && (
                <TableCell
                  rowSpan={bills.length}
                  sx={{ borderRight: `1px solid ${grey[300]}` }}
                >
                  <Typography
                    fontWeight={"bold"}
                    fontSize={13}
                    color={"text.secondary"}
                    onClick={() => dev_mode && getFileAgentChat(file["_id"])}
                    sx={{ wordBreak: "break-all" }}
                  >
                    {file["source_filename"]}
                  </Typography>
                </TableCell>
              )}

              <TableCell sx={{ wordBreak: "break-all" }}>
                {bill["vendor_name"] || "-"}
              </TableCell>
              <TableCell
                sx={{ wordBreak: "break-all" }}
                onClick={() => dev_mode && getBillAgentChat(bill["_id"])}
              >
                {bill["invoice_number"] || "-"}
              </TableCell>
              <TableCell sx={{ wordBreak: "break-all" }}>
                {bill["invoice_date"] || "-"}
              </TableCell>
              <TableCell>{bill["invoice_amount"] || "-"}</TableCell>

              <TableCell align="center">
                <Tooltip
                  title={
                    Array.isArray(bill.page_numbers)
                      ? bill.page_numbers.join(", ")
                      : "No pages"
                  }
                  arrow
                >
                  {status ? (
                    <Chip
                      color={status["color"]}
                      label={status["name"]}
                      size="small"
                    />
                  ) : (
                    <span>Processing</span>
                  )}
                </Tooltip>
              </TableCell>

              <TableCell align="center">
                <Tooltip
                  hidden={isApprove}
                  title={bill["error_description"]}
                  arrow
                >
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    onClick={() => setSelectedBill(bill)}
                    color={isApprove ? "success" : "primary"}
                    disabled={
                      !Boolean(status) || dev_mode
                        ? false
                        : [1, 2].includes(bill["bill_status"])
                    }
                  >
                    {isApprove ? "Posted" : "View"}
                  </Button>
                </Tooltip>
              </TableCell>

              {index === 0 && (
                <TableCell
                  rowSpan={bills.length}
                  sx={{ borderLeft: `1px solid ${grey[300]}` }}
                  align="center"
                >
                  {file["_id"] === downloadFile?.["_id"] ? (
                    <CircularProgress />
                  ) : (
                    <IconButton onClick={() => downloadS3File(file)}>
                      <img
                        src={DownloadIcon}
                        style={{ width: 22, height: 22 }}
                      />
                    </IconButton>
                  )}
                </TableCell>
              )}
            </TableRow>
          );
        })
      )}

      <Dialog open={selectedBill !== null} fullScreen>
        <AppBar position="static">
          <Toolbar variant="dense" sx={{ gap: 1 }}>
            <Stack direction={"row"} gap={1} alignItems={"center"} flexGrow={1}>
              <Typography variant="h5">Review</Typography>
              <Chip
                variant="outlined"
                size="small"
                label={selectedBill?.["source_filename"]}
                sx={{ color: "inherit" }}
                clickable
                onClick={() => {
                  navigator.clipboard.writeText(
                    selectedBill?.["source_filename"]
                  );
                  enqueueSnackbar("Text Copied", { variant: "info" });
                }}
              />
            </Stack>

            {dev_mode && (
              <IconButton
                color="inherit"
                onClick={() => getBillAgentChat(selectedBill["_id"])}
              >
                <Chat />
              </IconButton>
            )}

            <IconButton
              color="inherit"
              onClick={() => {
                setSelectedBill(null);
                refresh();
              }}
            >
              <Clear />
            </IconButton>
          </Toolbar>
        </AppBar>

        <DialogContent sx={{ p: 1 }}>
          {useMemo(() => {
            return selectedBill ? (
              <ReviewPage
                bill={selectedBill}
                file={file}
                email={email}
                close={() => {
                  setSelectedBill(null);
                  refresh();
                }}
              />
            ) : null;
          }, [selectedBill])}
        </DialogContent>
      </Dialog>

      <Dialog open={fileAgentChat !== null} fullScreen>
        <DialogContent sx={{ p: 1 }}>
          <AgentChat
            chat={fileAgentChat || {}}
            close={() => setFileAgentChat(null)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={billAgentChat !== null} fullScreen>
        <DialogContent sx={{ p: 1 }}>
          <AgentChat
            chat={billAgentChat || {}}
            close={() => setBillAgentChat(null)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
